import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../redux/UsersSlice';
import { Search, Check, X, Shield, MapPin, Building2, UserCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchSummary } from '../redux/SummarySlice';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import debounce from 'lodash/debounce';
import ExportUsersModal from '../components/ExportUsersModal';



const UserSearch = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const { loading } = useSelector((state) => state.users);


  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      dispatch(fetchUsers({
        page: 1,
        limit: 15,
        search: term,
        filter: '',
        filter_type: ''
      }));
    }, 500),
    [dispatch]
  );

  // Handle input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Clear search results when component unmounts
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="relative">
      <div className="relative">
        <Search
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${loading ? 'text-blue-500 animate-pulse' : 'text-gray-400'
            }`}
          size={20}
        />
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search users..."
          className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        />
      </div>
    </div>
  );
};

// Filter Component
const UserFilter = ({ onFilterChange, currentFilter }) => {
  return (
    <div className="flex space-x-2">
      {['all', 'active', 'inactive'].map(status => (
        <button
          key={status}
          onClick={() => onFilterChange(status)}
          className={`px-4 py-2 rounded-lg transition ${currentFilter === status
            ? 'bg-blue-500 text-white'
            : 'bg-gray-100 hover:bg-blue-500 hover:text-white'
            }`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </button>
      ))}
    </div>
  );
};
// User Card Component

const UserCard = ({ user }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/dashboard/users/${user.id}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
    >
      {/* Banner Image */}
      <div className="h-24 bg-gradient-to-r from-blue-100 to-blue-200 relative">
        {user?.banner_img && (
          <img
            src={user?.banner_img}
            alt="Profile Banner"
            className="w-full h-full object-cover"
          />
        )}

      </div>

      {/* Profile Section */}
      <div className="px-4 pb-4">
        {/* Profile Image - Positioned to overlap banner */}
        <div className="relative -mt-12 mb-3">


          {user?.profile_img ? (
            <img
              src={user?.profile_img}
              // alt={connection.name}
              className="w-20 h-20 rounded-full border-4 border-white shadow-md object-cover"
            />
          ) : (
            <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
              <UserCircle2 className="w-12 h-12 text-gray-400" />
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="space-y-2">
          <div>
            <h3 className="text-lg font-semibold">{`${user?.first_name} ${user?.last_name}`}</h3>
            <p className="text-gray-600 text-sm line-clamp-1">{user?.headline || 'Professional Headline'}</p>
          </div>

          {/* Location and Company */}
          <div className="space-y-1">
            {user?.location && (
              <div className="flex items-center text-gray-600 text-sm">
                <MapPin className="w-4 h-4 mr-1" />
                <span className='line-clamp-2'>{user.location}</span>
              </div>
            )}
            {user?.company && (
              <div className="flex items-center text-gray-600 text-sm">
                <Building2 className="w-4 h-4 mr-1" />
                <span>{user.company}</span>
              </div>
            )}
          </div>

          {/* Status Badge */}
          <div className="pt-2">
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${user?.status === 'active'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-red-100 text-red-800'
                }`}
            >
              {user?.status || 'Status'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};



// Main Users Page
const Users = () => {
  const dispatch = useDispatch();
  const users = useSelector(state => state.users.items);
  const {
    loading,
    error,
    meta
  } = useSelector((state) => state.users);

  const {
    pagination,
    handlePageChange,
    updatePaginationData,
    getPageNumbers,
    getItemRange
  } = usePagination({
    initialPage: meta.current_page || 1,
    initialPerPage: meta.per_page || 15
  });

  const [currentFilter, setCurrentFilter] = useState('all');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const openExportModal = () => {
    setExportModalOpen(true);
  };

  const closeExportModal = () => {
    setExportModalOpen(false);
  };

  useEffect(() => {
    handleFetchUsers();
  }, [pagination.currentPage, currentFilter]); // Add currentFilter to dependencies

  const handleFetchUsers = () => {
    const filterParams = currentFilter !== 'all'
      ? { filter: currentFilter, filter_type: 'status' }
      : {};

    dispatch(fetchUsers({
      page: pagination.currentPage,
      limit: pagination.perPage,
      ...filterParams
    }))
      .unwrap()
      .then(response => {
        if (response.meta) {
          updatePaginationData({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total
          });
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch users');
      });
  };

  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    handlePageChange(1);
  };


  return (
    <div className="">
      <div className="flex justify-between items-center mb-6 flex-col sm:flex-row gap-2">
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className='flex items-center gap-4'>
          <button
           onClick={openExportModal} 
            className='w-fit self-end font-lufga bg-gray-200 hover:opacity-90 focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center'
          >Export users
          </button>
          <UserSearch />
        </div>
      </div>

      <UserFilter
        onFilterChange={handleFilterChange}
        currentFilter={currentFilter}
      />

      {loading ? (
        <div className="flex justify-center items-center h-[80vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {users.map(user => (
            <UserCard key={user.id} user={user} />
          ))}
        </div>
      )}
      {!loading && users.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          No users found
        </div>
      )}

      <Pagination
        currentPage={pagination.currentPage}
        lastPage={pagination.lastPage}
        total={pagination.total}
        itemRange={getItemRange()}
        pageNumbers={getPageNumbers()}
        onPageChange={handlePageChange}
      />

<ExportUsersModal 
        isOpen={exportModalOpen}
        onClose={closeExportModal}
        currentFilter={currentFilter}
      />
    </div>
  );
};

export default Users;